import React, { useState, useEffect, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { getDateRangeDisplayLabel } from "./dateRangePresets";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { toast } from "react-toastify";
import {
  API_get_report_tabs,
  API_create_report_tab,
  API_update_report_tab,
  API_delete_report_tab,
  API_update_report_tabs_order,
  API_update_saved_report_tab,
} from "../../../constants";

// Fallback labels for employee metrics (used when availableMetrics doesn't include them)
const EMPLOYEE_METRICS_LABELS = {
  total_shifts_scheduled: { label: "Shifts Scheduled", format: "number" },
  total_scheduled_hours: { label: "Scheduled Hours", format: "hours" },
  actual_hours_clocked: { label: "Actual Hours", format: "hours" },
  total_shifts_worked: { label: "Shifts Worked", format: "number" },
  shifts_traded: { label: "Shifts Traded", format: "number" },
  time_off_taken: { label: "Time Off Days Taken", format: "number" },
  time_off_unpaid_taken: { label: "Unpaid Time Off Days Taken", format: "number" },
  time_off_pto_taken: { label: "PTO Days Taken", format: "number" },
  time_off_sick_taken: { label: "Sick Days Taken", format: "number" },
  missed_shifts: { label: "Shifts Missed (Legacy Combined)", format: "number" },
  unclassified_missed_shifts: { label: "Unclassified Missed Shifts (Needs Review)", format: "number" },
  called_out_sick_shifts: { label: "Called Out Sick (Explicit)", format: "number" },
  called_out_other_shifts: { label: "Called Out Other (Explicit)", format: "number" },
  no_call_no_show_shifts: { label: "No-Show (Explicit)", format: "number" },
  manager_cancelled_shifts: { label: "Manager Cancelled Shifts (Explicit)", format: "number" },
  unscheduled_shifts_worked: { label: "Unscheduled Shifts Worked", format: "number" },
  early_clockins: { label: "Early Clock-ins", format: "number" },
  late_clockins: { label: "Late Clock-ins", format: "number" },
  early_clockouts: { label: "Early Clock-outs", format: "number" },
  late_clockouts: { label: "Late Clock-outs", format: "number" },
  actual_to_scheduled_hours: { label: "Actual to Scheduled Hours", format: "percentage" },
  scheduled_to_target_hours: { label: "Scheduled to Target Hours", format: "percentage" },
  actual_to_target_hours: { label: "Actual to Target Hours", format: "percentage" },
  attendance_rate: { label: "Attendance Rate", format: "percentage" },
};

// Fallback labels for store metrics (used when availableMetrics doesn't include them)
const STORE_METRICS_LABELS = {
  forecasted_sales: { label: "Forecasted Sales", format: "dollars" },
  forecasted_adjusted_sales: { label: "Forecasted Adjusted Sales", format: "dollars" },
  actual_sales: { label: "Actual Sales", format: "dollars" },
  actual_sales_previous_year: { label: "Actual Sales (Previous Year)", format: "dollars" },
  actual_labor_cost: { label: "Actual Labor Cost", format: "dollars" },
  actual_sales_minus_labor_cost: { label: "Actual Sales - Labor Cost", format: "dollars" },
  labor_percent: { label: "Labor Percent", format: "percentage" },
  splh: { label: "Actual SPLH", format: "dollars" },
  forecasted_splh: { label: "Forecasted SPLH", format: "dollars" },
  scheduled_splh: { label: "Scheduled SPLH", format: "dollars" },
  actual_sales_vs_actual_sellable_hours: { label: "Actual Sales vs Actual Sellable Hours", format: "dollars" },
  earned_splh: { label: "Earned SPLH", format: "dollars" },
  scheduled_labor_hours: { label: "Scheduled Hours", format: "hours" },
  forecasted_labor_hours: { label: "Forecasted Hours", format: "hours" },
  actual_labor_hours: { label: "Actual Hours", format: "hours" },
  earned_labor_hours: { label: "Earned Hours", format: "hours" },
  logbook_entries: { label: "Logbook Entries", format: "number" },
  actual_to_forecasted_sales: { label: "Actual to Forecasted Sales", format: "percentage" },
  actual_to_forecasted_adjusted_sales: { label: "Actual to Forecasted Adjusted Sales", format: "percentage" },
  scheduled_to_forecasted_hours: { label: "Scheduled to Forecasted Hours", format: "percentage" },
  actual_to_earned_hours: { label: "Actual to Earned Hours", format: "percentage" },
  actual_to_scheduled_hours: { label: "Actual to Scheduled Hours", format: "percentage" },
  earned_to_forecasted_hours: { label: "Earned to Forecasted Hours", format: "percentage" },
};

function SavedReportsView({
  savedReports,
  savedReportsLoading,
  storeNames,
  availableMetrics,
  onDeleteReport,
  onReorderReports,
  onLoadReport = null, // Optional - only provided in ReportingDisplay
  showLoadButton = true, // Controls whether to show load button
  title = "Saved Reports To Dashboard",
  description = "You can go to the Dashboard tab in the sidebar to view all of these reports in one display.",
  onBack = null, // Function to call when back button is clicked
  backButtonText = "Back to Dashboard", // Text to show for back button
  homeStore = null, // Required for API calls
  onRefreshReports = null, // Callback to refresh reports data
  tabs = [], // Tabs passed from parent to avoid loading delay
  onRefreshTabs = null, // Callback to refresh tabs data
  isEmployeeReport = false, // When true, only employee reports can be loaded
  employees = [], // Employee list for resolving employee names in employee reports
}) {
  const axiosConfig = getDefaultAxiosConfig();
  const [localTabs, setLocalTabs] = useState(tabs);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null); // null = "All Reports"
  const [showTabDialog, setShowTabDialog] = useState(false);
  const [tabDialogMode, setTabDialogMode] = useState("create"); // "create" or "edit"
  const [editingTab, setEditingTab] = useState(null);
  const [tabName, setTabName] = useState("");
  const [tabSaveStatus, setTabSaveStatus] = useState("idle"); // "idle", "saving"

  // State for "Move to Tab" dropdown menu
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [reportToMove, setReportToMove] = useState(null);

  // Update local tabs when parent tabs change
  useEffect(() => {
    setLocalTabs(tabs);
  }, [tabs]);

  const fetchTabs = async () => {
    if (onRefreshTabs) {
      await onRefreshTabs();
    }
  };

  const handleCreateTab = () => {
    setTabDialogMode("create");
    setTabName("");
    setEditingTab(null);
    setTabSaveStatus("idle");
    setShowTabDialog(true);
  };

  const handleEditTab = (tab) => {
    setTabDialogMode("edit");
    setTabName(tab.name);
    setEditingTab(tab);
    setTabSaveStatus("idle");
    setShowTabDialog(true);
  };

  const handleTabDialogConfirm = async () => {
    if (!tabName.trim() || !homeStore || tabSaveStatus === "saving") return;

    setTabSaveStatus("saving");

    try {
      if (tabDialogMode === "create") {
        await axios.post(
          API_create_report_tab,
          { name: tabName.trim(), store_id: homeStore },
          axiosConfig
        );
      } else if (tabDialogMode === "edit" && editingTab) {
        await axios.put(
          `${API_update_report_tab}/${editingTab.id}`,
          { name: tabName.trim(), store_id: homeStore },
          axiosConfig
        );
      }
      setShowTabDialog(false);
      setTabName("");
      setTabSaveStatus("idle");
      // Refresh tabs from parent
      await fetchTabs();
    } catch (error) {
      console.error("Error saving tab:", error);
      setTabSaveStatus("idle");
    }
  };

  const handleDeleteTab = async (tabId) => {
    if (!window.confirm("Are you sure you want to delete this tab? Reports in this tab will not be deleted.")) {
      return;
    }

    try {
      await axios.delete(`${API_delete_report_tab}/${tabId}`, {
        params: { store_id: homeStore },
        ...axiosConfig,
      });
      if (selectedTab === tabId) {
        setSelectedTab(null);
      }
      // Refresh tabs from parent
      await fetchTabs();
    } catch (error) {
      console.error("Error deleting tab:", error);
    }
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    if (selectedTab === null) {
      // All reports view - indices match directly with the full savedReports array
      onReorderReports(result);
    } else {
      // Filtered view - need to translate indices from filtered list to full array
      const sourceReportId = filteredReports[result.source.index].id;
      const destinationReportId = filteredReports[result.destination.index].id;

      // Find the actual indices in the full savedReports array
      const actualSourceIndex = savedReports.findIndex(r => r.id === sourceReportId);
      const actualDestinationIndex = savedReports.findIndex(r => r.id === destinationReportId);

      // Create a modified result with the actual indices
      const translatedResult = {
        ...result,
        source: {
          ...result.source,
          index: actualSourceIndex
        },
        destination: {
          ...result.destination,
          index: actualDestinationIndex
        }
      };

      onReorderReports(translatedResult);
    }
  };

  const handleOpenMoveMenu = (event, report) => {
    setMoveMenuAnchor(event.currentTarget);
    setReportToMove(report);
  };

  const handleCloseMoveMenu = () => {
    setMoveMenuAnchor(null);
    setReportToMove(null);
  };

  const handleMoveToTab = async (tabId) => {
    if (!reportToMove) return;

    // Close the menu immediately
    handleCloseMoveMenu();

    // Show loading toast
    toast.loading("Moving report...", { autoClose: false });

    try {
      await axios.put(
        `${API_update_saved_report_tab}/${reportToMove.id}`,
        {
          tab_id: tabId,
          store_id: homeStore
        },
        axiosConfig
      );

      // Refresh tabs and reports data
      await fetchTabs();
      if (onRefreshReports) {
        await onRefreshReports();
      }

      // Dismiss loading toast and show success
      toast.dismiss();
      toast.success("Report moved successfully!", { autoClose: 3000 });
    } catch (error) {
      console.error("Error moving report to tab:", error);
      // Dismiss loading toast and show error
      toast.dismiss();
      toast.error("Failed to move report", { autoClose: 3000 });
    }
  };

  // Filter reports based on selected tab
  const getFilteredReports = () => {
    if (selectedTab === null) {
      // "All Reports" - show all
      return savedReports;
    }
    // Show only reports belonging to the selected tab
    return savedReports.filter((report) => report.tab_id === selectedTab);
  };

  const filteredReports = getFilteredReports();

  // Helper functions for report data (defined before renderReportCard which uses them)
  const getMetricInfoForReport = useCallback((metricValue) => {
    if (!metricValue) {
      return { label: "Unknown Metric", format: "number" };
    }

    let baseValue = metricValue;
    let suffix = "";

    if (metricValue.endsWith("_previous_year")) {
      baseValue = metricValue.replace("_previous_year", "");
      suffix = " (Prev Year)";
    }

    const metric = availableMetrics.find((m) => m.value === baseValue);

    if (metric) {
      return {
        label: `${metric.label}${suffix}`,
        format: metric.format,
      };
    }

    // Fallback to employee metrics labels
    const employeeMetric = EMPLOYEE_METRICS_LABELS[baseValue];
    if (employeeMetric) {
      return {
        label: `${employeeMetric.label}${suffix}`,
        format: employeeMetric.format,
      };
    }

    // Fallback to store metrics labels
    const storeMetric = STORE_METRICS_LABELS[baseValue];
    if (storeMetric) {
      return {
        label: `${storeMetric.label}${suffix}`,
        format: storeMetric.format,
      };
    }

    return { label: metricValue, format: "number" };
  }, [availableMetrics]);

  const formatDateRangeForDisplay = useCallback((dateRangeString) =>
    getDateRangeDisplayLabel(dateRangeString), []);

  const getStoreNamesForReport = useCallback((report) => {
    // For employee reports, show employee names or count
    if (report.report_type === "employee") {
      if (!report.selected_employees || report.selected_employees.length === 0) {
        return "No employees selected";
      }
      // Use stored names if available
      if (report.selected_employee_names && report.selected_employee_names.length > 0) {
        return report.selected_employee_names.join(", ");
      }
      // Try to resolve employee names from the employees list
      if (employees && employees.length > 0) {
        const selectedKeys = new Set(report.selected_employees.map(String));
        const selectedEmployeeNames = employees
          .filter(emp => selectedKeys.has(emp.selection_key))
          .map(emp => `${emp.first_name} ${emp.last_name}`);
        if (selectedEmployeeNames.length > 0) {
          return selectedEmployeeNames.join(", ");
        }
      }
      // Fall back to count if names can't be resolved
      return `${report.selected_employees.length} employee${report.selected_employees.length !== 1 ? 's' : ''}`;
    }

    // For store reports
    if (!storeNames || !report.selected_stores) {
      return "No stores selected";
    }
    return storeNames
      .filter(store => report.selected_stores.includes(store.id))
      .map(store => store.store_name)
      .join(", ");
  }, [employees, storeNames]);

  // Helper function to render a report card (used both in list and for drag clone)
  const renderReportCard = useCallback((report, index, provided, snapshot, reportsList) => {
    const compareLocationsChecked = report.selected_radio_option === "Compare by Store" ||
      report.selected_radio_option === "Compare by Employee" ||
      report.selected_radio_option === "Compare by Role";
    const compareDow = report.selected_radio_option === "Compare by Day Of Week";
    const compareMetricsChecked = report.selected_radio_option === "Compare by Metric";

    const getComparisonLabel = () => {
      switch (report.selected_radio_option) {
        case "Compare by Store": return "Comparing Stores";
        case "Compare by Employee": return "Comparing Employees";
        case "Compare by Role": return "Comparing Roles";
        case "Compare by Metric": return "Comparing Metrics";
        default: return "Comparing";
      }
    };

    // Find the display index (position in the current list view)
    const displayIndex = reportsList ? reportsList.findIndex(r => r.id === report.id) : index;

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        style={provided.draggableProps.style}
        className={`flex items-center gap-4 p-4 transition-all ${
          snapshot.isDragging
            ? "bg-violet-50 border border-violet-200 shadow-lg rounded-lg"
            : "bg-white hover:bg-gray-50"
        }`}
      >
        <div
          className="text-gray-400 cursor-grab hover:text-gray-600 transition-colors"
          {...provided.dragHandleProps}
        >
          <DragIndicatorIcon />
        </div>

        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          report.report_type === "logbook"
            ? "bg-orange-100"
            : report.report_type === "employee"
            ? "bg-teal-100"
            : "bg-violet-100"
        }`}>
          <span className={`text-sm font-semibold ${
            report.report_type === "logbook"
              ? "text-orange-700"
              : report.report_type === "employee"
              ? "text-teal-700"
              : "text-violet-700"
          }`}>
            {displayIndex + 1}
          </span>
        </div>

        <div className="flex-1">
          <div className="min-w-0">
            <h4 className="font-medium text-gray-900 truncate">{report.name}</h4>
            <p className="text-sm text-gray-600 break-words">
              {getStoreNamesForReport(report)}
            </p>
            <p className="text-xs text-gray-500">
              Time Period: {formatDateRangeForDisplay(report.selected_date_range)}
            </p>
            {report.report_type === "logbook" ? (
              <p className="text-xs mt-1">
                <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">
                  Logbook
                </span>
              </p>
            ) : report.report_type === "employee" ? (
              <p className="text-xs text-gray-500 break-words">
                {report.selected_metrics?.length === 1
                  ? `Metric: ${getMetricInfoForReport(report.selected_metrics[0])?.label || "Unknown"}`
                  : `Metrics: ${report.selected_metrics?.map(m => getMetricInfoForReport(m).label).join(", ")}`}
              </p>
            ) : (
              <p className="text-xs text-gray-500 break-words">
                {report.selected_metrics?.length === 1
                  ? `Metric: ${getMetricInfoForReport(report.selected_metrics[0])?.label || "Unknown"}`
                  : `Metrics: ${report.selected_metrics?.map(m => getMetricInfoForReport(m).label).join(", ")}`}
              </p>
            )}
            {report.report_type !== "logbook" && (
              <p className="text-xs text-blue-600 font-medium">
                {(compareLocationsChecked || compareDow || compareMetricsChecked)
                  ? `${(compareLocationsChecked || compareMetricsChecked) ? getComparisonLabel() : "Comparing Days of Week"} • ${report.active_tab || "Daily"}`
                  : report.active_tab || "Daily"}
              </p>
            )}
            {report.report_type === "employee" && (
              <p className="text-xs mt-1">
                <span className="inline-block px-2 py-0.5 bg-teal-100 text-teal-700 rounded font-medium">
                  Employee
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0 items-start">
          {showLoadButton && onLoadReport && (
            isEmployeeReport
              ? report.report_type === "employee"
              : report.report_type !== "employee" && report.report_type !== "logbook"
          ) && (
            <button
              className="px-3 py-1.5 text-sm rounded-md text-violet-600 hover:bg-violet-50 transition-colors whitespace-nowrap"
              onClick={() => onLoadReport(report)}
            >
              Load
            </button>
          )}
          <Tooltip title="Move to tab" arrow>
            <IconButton
              size="small"
              onClick={(e) => handleOpenMoveMenu(e, report)}
              className="text-gray-500 hover:bg-gray-100 flex-shrink-0"
            >
              <FolderIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete report" arrow>
            <IconButton
              size="small"
              onClick={() => onDeleteReport(report.id)}
              className="text-red-500 hover:bg-red-50 flex-shrink-0"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  }, [showLoadButton, onLoadReport, isEmployeeReport, onDeleteReport, handleOpenMoveMenu, getStoreNamesForReport, formatDateRangeForDisplay, getMetricInfoForReport]);

  return (
    <div className="bg-white min-h-screen">
      <TopBarDisplayTitleNoLocationSelect
        title={backButtonText}
        showBackButton={true}
        onBackClick={onBack}
      />

      <div className="px-6 pt-6 pb-8">
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          </div>
          <p className="text-sm text-gray-600">
            {description}
          </p>
        </div>

        {/* Tab Navigation */}
        {localTabs.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 border-b border-gray-200">
              {/* All Reports Tab */}
              <button
                onClick={() => setSelectedTab(null)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  selectedTab === null
                    ? "border-violet-600 text-violet-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                All Reports ({savedReports.length})
              </button>

              {/* User-created tabs */}
              {localTabs.map((tab) => (
                <div key={tab.id} className="flex items-center group">
                  <button
                    onClick={() => setSelectedTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      selectedTab === tab.id
                        ? "border-violet-600 text-violet-600"
                        : "border-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab.name} ({tab.report_count || 0})
                  </button>
                  <div className="ml-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <IconButton
                      size="small"
                      onClick={() => handleEditTab(tab)}
                      className="text-gray-400 hover:text-gray-600"
                      sx={{ padding: "2px" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTab(tab.id)}
                      className="text-red-400 hover:text-red-600"
                      sx={{ padding: "2px" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}

              {/* Add Tab Button */}
              <button
                onClick={handleCreateTab}
                className="ml-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors flex items-center gap-1"
              >
                <AddIcon fontSize="small" />
                New Tab
              </button>
            </div>
          </div>
        )}

        {/* Create/Edit Tab Button (shown when no tabs exist) */}
        {localTabs.length === 0 && !tabsLoading && savedReports.length > 0 && (
          <div className="mb-4">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateTab}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#6d28d9",
                color: "#6d28d9",
                "&:hover": {
                  borderColor: "#5b21b6",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Create Tab to Organize Reports
            </Button>
          </div>
        )}

        <div>
          {savedReportsLoading ? (
            <div className="text-center py-8">
              <div className="flex items-center justify-center">
                <LoadSpinner />
              </div>
              <p className="text-gray-500 mt-2">Loading saved reports...</p>
            </div>
          ) : savedReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No saved reports found. Create and save a report to see it here.
            </div>
          ) : (
            <DragDropContext onDragEnd={handleOnDragEnd}>
              {selectedTab === null ? (
                // Show all reports when "All Reports" is selected
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Droppable
                    droppableId="all-reports"
                    renderClone={(provided, snapshot, rubric) => {
                      const report = savedReports[rubric.source.index];
                      return renderReportCard(report, rubric.source.index, provided, snapshot, savedReports);
                    }}
                  >
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="divide-y divide-gray-200"
                      >
                        {savedReports.map((report, index) => (
                          <Draggable
                            key={report.id}
                            draggableId={`report-${report.id}`}
                            index={index}
                          >
                            {(provided, snapshot) => renderReportCard(report, index, provided, snapshot, savedReports)}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No reports in this tab. Move reports here or assign reports to this tab when saving.
                </div>
              ) : (
                // Show reports for the selected tab
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Droppable
                    droppableId={`tab-${selectedTab}`}
                    renderClone={(provided, snapshot, rubric) => {
                      const report = filteredReports[rubric.source.index];
                      return renderReportCard(report, rubric.source.index, provided, snapshot, filteredReports);
                    }}
                  >
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="divide-y divide-gray-200"
                      >
                        {filteredReports.map((report, index) => (
                          <Draggable
                            key={report.id}
                            draggableId={`report-${report.id}`}
                            index={index}
                          >
                            {(provided, snapshot) => renderReportCard(report, index, provided, snapshot, filteredReports)}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              )}
            </DragDropContext>
          )}
        </div>
      </div>

      {/* Tab Create/Edit Modal */}
      <Modal
        open={showTabDialog}
        onClose={() => setShowTabDialog(false)}
        aria-labelledby="tab-modal"
        aria-describedby="tab-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            bgcolor: "background.paper",
            border: "2px solid #E2E8F0",
            borderRadius: "15px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setShowTabDialog(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-2">
              <h3 className="text-xl font-semibold text-gray-900">
                {tabDialogMode === "create" ? "Create New Tab" : "Rename Tab"}
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-4">
                {tabDialogMode === "create"
                  ? "Create a new tab to organize your saved reports into categories."
                  : "Enter a new name for this tab."}
              </p>

              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Enter tab name"
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && tabName.trim()) {
                    handleTabDialogConfirm();
                  }
                }}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "5px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={() => setShowTabDialog(false)}
                disabled={tabSaveStatus === "saving"}
              >
                Cancel
              </button>
              <button
                onClick={handleTabDialogConfirm}
                disabled={!tabName.trim() || tabSaveStatus === "saving"}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center
                  ${tabName.trim() && tabSaveStatus !== "saving"
                    ? 'bg-violet-700 text-white hover:bg-violet-800'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              >
                {tabSaveStatus === "saving" ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {tabDialogMode === "create" ? "Creating..." : "Saving..."}
                  </>
                ) : (
                  tabDialogMode === "create" ? "Create" : "Save"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Move to Tab Menu */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={handleCloseMoveMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleMoveToTab(null)}>
          <em>No tab (All Reports)</em>
        </MenuItem>
        {localTabs.map((tab) => (
          <MenuItem
            key={tab.id}
            onClick={() => handleMoveToTab(tab.id)}
            disabled={reportToMove?.tab_id === tab.id}
          >
            {tab.name}
            {reportToMove?.tab_id === tab.id && " (current)"}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export default SavedReportsView;
